/**
 * Tenant URL segment parsing (path-only; no membership resolution).
 *
 * Authenticated customer routes live under `/~/:tenantSlug/...`.
 * Product-wide authenticated globals (`/whats-new`, `/login-success`, `/sentry`)
 * do not.
 * Membership is enforced by `TenantRouteGate`; slug↔id mapping lives in
 * `tenant-routing.ts`.
 *
 * Path segments are percent-encoded when emitted and percent-decoded when
 * read so reserved characters round-trip through email clients.
 */
export const TENANT_SCOPE = '~' as const;
export const TENANT_SLUG_PARAM = ':tenantSlug' as const;
export const TENANT_ROOT = `/${TENANT_SCOPE}/${TENANT_SLUG_PARAM}` as const;

export const TENANT_PATH_PREFIX = `/~/` as const;

/** TODO(2026-11): remove with aliasLegacyTenantSlug once TI Communities email links have aged out. */
export const LEGACY_TI_COMMUNITIES_SLUG = 'ti0198f7]';
export const CANONICAL_TI_COMMUNITIES_SLUG = 'ti0198f7';

/**
 * Maps the one-off TI Communities typo slug to the canonical slug.
 *
 * TODO(2026-11): remove once email links with `ti0198f7]` have aged out.
 */
export function aliasLegacyTenantSlug(slug: string): string {
  if (slug === LEGACY_TI_COMMUNITIES_SLUG) {
    return CANONICAL_TI_COMMUNITIES_SLUG;
  }
  if (slug === 'ti0198f7%5D' || slug === 'ti0198f7%5d') {
    return CANONICAL_TI_COMMUNITIES_SLUG;
  }
  return slug;
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Reads the tenant slug as the path segment immediately after `/~/`, or returns
 * null for non-tenant URLs (legacy tenantless paths, `/login`, etc.).
 * Percent-decodes the segment so encoded email/in-app URLs match stored slugs.
 */
export function readTenantSlugFromPath(pathname: string): string | null {
  if (!pathname || typeof pathname !== 'string') return null;
  if (!pathname.startsWith(TENANT_PATH_PREFIX)) return null;
  const rest = pathname.slice(TENANT_PATH_PREFIX.length);
  if (!rest) return null;
  const slash = rest.indexOf('/');
  const rawSegment = slash === -1 ? rest : rest.slice(0, slash);
  if (!rawSegment) return null;
  return aliasLegacyTenantSlug(decodePathSegment(rawSegment));
}

export function readTenantSlugFromCurrentUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return readTenantSlugFromPath(window.location.pathname);
}

/** Materializes `/~/{slug}` for a slug already validated by tenant-routing policy. */
export function buildTenantRootPath(tenantSlug: string): string {
  return `${TENANT_PATH_PREFIX}${encodeURIComponent(tenantSlug)}`;
}

/**
 * Returns the path after `/~/{slug}` (including leading slash), or null if
 * the pathname is not tenant-scoped.
 *
 * Slices after the first segment so encoded slugs (`ti0198f7%5D`) still yield
 * the feature suffix — do not reconstruct the prefix from the decoded slug.
 */
export function readTenantSuffixFromPath(pathname: string): string | null {
  if (!pathname || typeof pathname !== 'string') return null;
  if (!pathname.startsWith(TENANT_PATH_PREFIX)) return null;
  const rest = pathname.slice(TENANT_PATH_PREFIX.length);
  if (!rest) return null;
  const slash = rest.indexOf('/');
  if (slash === -1) return '/';
  return rest.slice(slash);
}

/**
 * Extracts a property id from tenantless or tenant-scoped customer paths.
 * Returns null when the pathname is not property-scoped.
 */
export function readPropertyIdFromCustomerPath(
  pathname: string
): string | null {
  if (!pathname || typeof pathname !== 'string') return null;
  const suffix = readTenantSuffixFromPath(pathname) ?? pathname;

  const propertiesMatch = suffix.match(/^\/properties\/([^/]+)/);
  if (propertiesMatch?.[1]) return propertiesMatch[1];

  const documentManagementMatch = suffix.match(
    /^\/document-management\/([^/]+)/
  );
  if (documentManagementMatch?.[1]) return documentManagementMatch[1];

  const adminProspectiveMatch = suffix.match(
    /^\/admin\/properties\/prospective\/([^/]+)/
  );
  if (adminProspectiveMatch?.[1]) return adminProspectiveMatch[1];

  return null;
}
